import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  BookOpen, Calendar, Users, TrendingUp, CheckCircle2, Clock, 
  RotateCcw, Circle, ChevronRight, FileText, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api, { studentApi, quranicProgressApi, surahApi } from '../lib/api';

// Status colors
const STATUS_CONFIG = {
  suivi: { label: 'Suivi (حـ)', color: 'bg-green-100 text-green-700' },
  non_suivi: { label: 'Non suivi (لم)', color: 'bg-red-100 text-red-700' },
  absent: { label: 'Absent (غ)', color: 'bg-gray-100 text-gray-700' },
  malade: { label: 'Malade (م)', color: 'bg-yellow-100 text-yellow-700' },
};

const GRADE_CONFIG = {
  weak: { label: 'Faible (ضعيف)', color: 'bg-red-100 text-red-700' },
  acceptable: { label: 'Acceptable (مقبول)', color: 'bg-yellow-100 text-yellow-700' },
  good: { label: 'Bien (جيد)', color: 'bg-blue-100 text-blue-700' },
  very_good: { label: 'Très bien (جيد جدا)', color: 'bg-green-100 text-green-700' },
};

const PROGRESS_STATUS = {
  not_started: { label: 'Non commencé', icon: Circle, color: 'text-gray-400' },
  in_progress: { label: 'En cours', icon: Clock, color: 'text-blue-500' },
  acquired: { label: 'Acquis', icon: CheckCircle2, color: 'text-green-500' },
  revision: { label: 'Révision', icon: RotateCcw, color: 'text-orange-500' },
};

export default function StudentQuranProfilePage() {
  const { id: studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [surahs, setSurahs] = useState([]);
  const [progress, setProgress] = useState([]);
  const [dailyEvals, setDailyEvals] = useState([]);
  const [monthlyEvals, setMonthlyEvals] = useState([]);
  const [trimEvals, setTrimEvals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (studentId) {
      fetchAllData();
    }
  }, [studentId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [studentRes, surahsRes, progressRes, dailyRes, monthlyRes, trimRes] = await Promise.all([
        studentApi.getById(studentId),
        surahApi.getAll(),
        quranicProgressApi.getAll({ student_id: studentId }),
        api.get(`/quranic-daily-evals?student_id=${studentId}`),
        api.get(`/monthly-evals?student_id=${studentId}`),
        api.get(`/trimestrial-evals?student_id=${studentId}`),
      ]);
      
      setStudent(studentRes.data);
      setSurahs(surahsRes.data);
      setProgress(progressRes.data);
      setDailyEvals(dailyRes.data);
      setMonthlyEvals(monthlyRes.data);
      setTrimEvals(trimRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const getStats = () => {
    const acquiredSurahs = progress.filter(p => p.status === 'acquired').length;
    const inProgressSurahs = progress.filter(p => p.status === 'in_progress').length;
    const totalVerses = surahs.reduce((acc, s) => acc + s.verses, 0);
    const memorizedVerses = progress.reduce((acc, p) => acc + (p.verses_memorized || 0), 0);
    
    const dailySuivi = dailyEvals.filter(e => e.status === 'suivi').length;
    const dailyTotal = dailyEvals.length;
    
    return {
      acquiredSurahs,
      inProgressSurahs,
      totalSurahs: surahs.length,
      memorizedVerses,
      totalVerses,
      progressPercent: totalVerses > 0 ? Math.round((memorizedVerses / totalVerses) * 100) : 0,
      dailySuiviPercent: dailyTotal > 0 ? Math.round((dailySuivi / dailyTotal) * 100) : 0,
      monthlyCount: monthlyEvals.length,
      trimCount: trimEvals.length,
    };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Élève non trouvé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="student-quran-profile">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-primary" />
            Profil Coranique
          </h1>
          <p className="text-muted-foreground">
            {student.first_name} {student.last_name} - {student.matricule}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to={`/students/${studentId}`}>
              <FileText className="w-4 h-4 mr-2" />
              Fiche élève
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{stats.acquiredSurahs}</div>
            <p className="text-xs text-muted-foreground">Sourates acquises</p>
            <Progress value={(stats.acquiredSurahs / 114) * 100} className="mt-2 h-1" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{stats.progressPercent}%</div>
            <p className="text-xs text-muted-foreground">Progression versets</p>
            <Progress value={stats.progressPercent} className="mt-2 h-1" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-purple-600">{stats.dailySuiviPercent}%</div>
            <p className="text-xs text-muted-foreground">Taux de suivi quotidien</p>
            <Progress value={stats.dailySuiviPercent} className="mt-2 h-1" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-orange-600">{stats.trimCount}</div>
            <p className="text-xs text-muted-foreground">Éval. trimestrielles</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="daily">Quotidien</TabsTrigger>
          <TabsTrigger value="monthly">Mensuel</TabsTrigger>
          <TabsTrigger value="trimestrial">Trimestriel</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Progression par Sourate</CardTitle>
              <CardDescription>État d'avancement des 114 sourates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto">
                {surahs.slice(0, 20).map(surah => {
                  const surahProgress = progress.find(p => p.surah_id === surah.id);
                  const status = surahProgress?.status || 'not_started';
                  const StatusIcon = PROGRESS_STATUS[status]?.icon || Circle;
                  return (
                    <div key={surah.id} className="flex items-center gap-2 p-2 rounded border">
                      <StatusIcon className={`w-4 h-4 ${PROGRESS_STATUS[status]?.color}`} />
                      <span className="text-sm truncate">{surah.id}. {surah.name_arabic}</span>
                    </div>
                  );
                })}
              </div>
              {surahs.length > 20 && (
                <p className="text-center text-sm text-muted-foreground mt-2">
                  Et {surahs.length - 20} autres sourates...
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Dernières éval. quotidiennes</CardTitle>
              </CardHeader>
              <CardContent>
                {dailyEvals.slice(0, 5).map((evalItem, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                    <div>
                      <Badge variant="outline" className="text-xs">{evalItem.eval_type}</Badge>
                      <span className="text-xs text-muted-foreground ml-2">{evalItem.evaluation_date}</span>
                    </div>
                    <Badge className={STATUS_CONFIG[evalItem.status]?.color || ''}>
                      {STATUS_CONFIG[evalItem.status]?.label || evalItem.status}
                    </Badge>
                  </div>
                ))}
                {dailyEvals.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">Aucune évaluation</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Évaluations trimestrielles</CardTitle>
              </CardHeader>
              <CardContent>
                {trimEvals.map((evalItem, idx) => {
                  const grades = evalItem.parts?.filter(p => p.grade).length || 0;
                  const veryGood = evalItem.parts?.filter(p => p.grade === 'very_good').length || 0;
                  return (
                    <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                      <div>
                        <span className="font-medium">Trimestre {evalItem.trimester}</span>
                        <span className="text-xs text-muted-foreground ml-2">{evalItem.school_year}</span>
                      </div>
                      <Badge variant="secondary">{veryGood}/{grades} جيد جدا</Badge>
                    </div>
                  );
                })}
                {trimEvals.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">Aucune évaluation</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Daily Tab */}
        <TabsContent value="daily">
          <Card>
            <CardHeader>
              <CardTitle>Évaluations Quotidiennes (Bind / Wett / Nafarr)</CardTitle>
              <CardDescription>Historique des évaluations quotidiennes</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Détails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyEvals.map((evalItem, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{evalItem.evaluation_date}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{evalItem.eval_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_CONFIG[evalItem.status]?.color || ''}>
                          {STATUS_CONFIG[evalItem.status]?.label || evalItem.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {evalItem.eval_type === 'bind' && evalItem.surah_id && (
                          <>Sourate {evalItem.surah_id}, v.{evalItem.verse_start}-{evalItem.verse_end}</>
                        )}
                        {(evalItem.eval_type === 'wett' || evalItem.eval_type === 'nafarr') && evalItem.hizb_start && (
                          <>Hizb {evalItem.hizb_start}-{evalItem.hizb_end}</>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {dailyEvals.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Aucune évaluation quotidienne</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Tab */}
        <TabsContent value="monthly">
          <Card>
            <CardHeader>
              <CardTitle>Évaluations Mensuelles (مراقبات شهرية)</CardTitle>
              <CardDescription>Suivi mensuel par l'examinateur</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mois/Année</TableHead>
                    <TableHead>Niveau (المستوى)</TableHead>
                    <TableHead>Retards</TableHead>
                    <TableHead>Dernière leçon</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyEvals.map((evalItem, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{evalItem.month}/{evalItem.year}</TableCell>
                      <TableCell dir="rtl">{evalItem.current_level || '-'}</TableCell>
                      <TableCell>
                        {(evalItem.absences_day || 0) + (evalItem.absences_morning || 0) + (evalItem.absences_afternoon || 0)}
                      </TableCell>
                      <TableCell dir="rtl">{evalItem.last_lesson || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {monthlyEvals.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Aucune évaluation mensuelle</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trimestrial Tab */}
        <TabsContent value="trimestrial">
          <Card>
            <CardHeader>
              <CardTitle>Évaluations Trimestrielles</CardTitle>
              <CardDescription>EVALUATION DES ELEVES CORANIQUES - 12 parties</CardDescription>
            </CardHeader>
            <CardContent>
              {trimEvals.map((evalItem, idx) => (
                <div key={idx} className="mb-6 p-4 border rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium">
                      Trimestre {evalItem.trimester} - {evalItem.school_year}
                    </h3>
                    <Button variant="outline" size="sm" asChild>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/trimestrial-evals/${evalItem.id}/pdf`} target="_blank" rel="noreferrer">
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </a>
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
                    {evalItem.parts?.map((part, pIdx) => (
                      <div key={pIdx} className={`p-2 rounded text-center text-sm ${GRADE_CONFIG[part.grade]?.color || 'bg-gray-50'}`}>
                        <div className="font-bold">{part.part_number}</div>
                        <div className="text-xs truncate">{part.surah_name || '-'}</div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <span className="text-sm text-muted-foreground">التجويد: </span>
                      <Badge className={GRADE_CONFIG[evalItem.tajweed_grade]?.color || ''}>
                        {GRADE_CONFIG[evalItem.tajweed_grade]?.label || '-'}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">المنهج: </span>
                      <Badge className={GRADE_CONFIG[evalItem.curriculum_grade]?.color || ''}>
                        {GRADE_CONFIG[evalItem.curriculum_grade]?.label || '-'}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
              {trimEvals.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Aucune évaluation trimestrielle</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
